import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import toast from "react-hot-toast";
import { compareAPI } from "../services/api";

export default function ComparePage({ documents, compareState, setCompareState }) {
  const [loading, setLoading] = useState(false);
  const query = compareState.query;
  const selectedDocs = compareState.selectedDocs;
  const result = compareState.result;
  const setQuery = v => setCompareState(p=>({...p,query:v}));
  const setSelectedDocs = v => setCompareState(p=>({...p,selectedDocs:typeof v==="function"?v(p.selectedDocs):v}));
  const setResult = v => setCompareState(p=>({...p,result:v}));
  const toggle = id => setSelectedDocs(p=>p.includes(id)?p.filter(d=>d!==id):[...p,id]);

  const compare = async () => {
    if(selectedDocs.length<2) return toast.error("Select at least 2 documents");
    if(!query.trim()) return toast.error("Enter a query");
    setLoading(true);
    try { const r=await compareAPI.compare({query,doc_ids:selectedDocs}); setResult(r.data); }
    catch { toast.error("Comparison failed"); }
    finally { setLoading(false); }
  };

  if(!documents.length) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100%", color:"var(--text-3)", textAlign:"center" }}>
      <div style={{ fontSize:"36px", marginBottom:"12px" }}>📑</div>
      <p style={{ fontSize:"14px", color:"var(--text-2)" }}>Upload at least 2 documents to compare</p>
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden", background:"var(--bg)" }}>
      <div style={{ padding:"20px 24px", borderBottom:"1px solid var(--border)", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"4px" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" stroke="#14b88e" strokeWidth="2" strokeLinecap="round"/></svg>
          <h2 style={{ fontSize:"18px", fontWeight:700, color:"var(--text-1)" }}>Compare Documents</h2>
        </div>
        <p style={{ fontSize:"12px", color:"var(--text-3)", marginBottom:"16px" }}>Select 2+ documents and ask a question to get a structured side-by-side analysis</p>

        <div style={{ marginBottom:"12px" }}>
          <div style={{ fontSize:"11px", fontWeight:600, color:"var(--accent)", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:"8px" }}>
            Select Documents ({selectedDocs.length} Selected)
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
            {documents.map(doc => {
              const sel = selectedDocs.includes(doc.id);
              return (
                <button key={doc.id} onClick={()=>toggle(doc.id)}
                  style={{ display:"flex", alignItems:"center", gap:"6px", padding:"6px 14px", borderRadius:"8px", border:`1px solid ${sel?"var(--accent)":"var(--border)"}`, background:sel?"var(--accent-bg)":"transparent", color:sel?"var(--accent)":"var(--text-2)", cursor:"pointer", fontSize:"13px", fontWeight:500, transition:"all 0.15s", fontFamily:"inherit" }}>
                  {sel ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" stroke="#14b88e" strokeWidth="2"/><path d="M9 12l2 2 4-4" stroke="#14b88e" strokeWidth="2" strokeLinecap="round"/></svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" stroke="#7d8590" strokeWidth="2"/></svg>
                  )}
                  <span style={{ maxWidth:"140px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{doc.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display:"flex", gap:"10px" }}>
          <input value={query} onChange={e=>setQuery(e.target.value)} onKeyDown={e=>e.key==="Enter"&&compare()}
            placeholder="compare the documents" className="input-base" style={{ flex:1, borderRadius:"12px" }} />
          <button onClick={compare} disabled={loading||selectedDocs.length<2||!query.trim()}
            style={{ display:"flex", alignItems:"center", gap:"8px", padding:"10px 24px", borderRadius:"12px", background:"var(--accent)", color:"white", border:"none", cursor:"pointer", fontSize:"14px", fontWeight:600, fontFamily:"inherit", boxShadow:"0 2px 8px rgba(20,184,142,0.3)", opacity:(loading||selectedDocs.length<2||!query.trim())?0.5:1 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M12 5l7 7-7 7" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>
            {loading ? "Comparing..." : "Compare"}
          </button>
        </div>
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"20px 24px" }}>
        {loading && <div style={{ textAlign:"center", padding:"60px", color:"var(--text-3)", fontSize:"14px" }}>Analyzing documents...</div>}

        {result && !loading && (
          <div style={{ maxWidth:"900px" }}>
            {result.rewritten_query !== result.query && (
              <div style={{ fontSize:"12px", color:"var(--accent)", marginBottom:"16px", display:"flex", alignItems:"center", gap:"6px" }}>
                <span style={{ color:"var(--text-3)" }}>Optimized query:</span>
                <span style={{ fontFamily:"monospace", color:"var(--text-2)", background:"var(--surface)", padding:"2px 8px", borderRadius:"4px", border:"1px solid var(--border)" }}>"{result.rewritten_query}"</span>
              </div>
            )}

            <div style={{ display:"grid", gridTemplateColumns:`repeat(${result.doc_results.length},1fr)`, gap:"12px", marginBottom:"20px" }}>
              {result.doc_results.map(dr=>(
                <div key={dr.doc_id} style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"10px", padding:"14px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"8px" }}>
                    <span style={{ fontSize:"13px", fontWeight:600, color:"var(--text-1)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"70%" }}>{dr.doc_name}</span>
                    <span style={{ fontSize:"12px", fontWeight:700, color:Math.round(dr.confidence*100)>=50?"var(--accent)":"#f59e0b" }}>{Math.round(dr.confidence*100)}% conf.</span>
                  </div>
                  {dr.top_chunks.map((t,i)=><p key={i} style={{ fontSize:"11px", color:"var(--text-3)", lineHeight:"1.5", marginBottom:"3px", overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>{t}</p>)}
                </div>
              ))}
            </div>

            <div style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"12px", padding:"20px" }}>
              <div style={{ fontSize:"14px", fontWeight:700, color:"var(--text-1)", marginBottom:"14px", display:"flex", alignItems:"center", gap:"8px" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" stroke="#14b88e" strokeWidth="2" strokeLinecap="round"/></svg>
                Comparative Analysis
              </div>
              <div className="prose-out">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.comparison}</ReactMarkdown>
              </div>
            </div>
          </div>
        )}

        {!result && !loading && (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100%", color:"var(--text-3)", textAlign:"center" }}>
            <div style={{ fontSize:"36px", marginBottom:"12px" }}>📊</div>
            <p style={{ fontSize:"13px" }}>Select documents and enter a query to compare</p>
          </div>
        )}
      </div>
    </div>
  );
}